import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Heading, Text, useToast, Spinner } from '@chakra-ui/react';
import TwellaLogoIcon from 'assets/icons/twellaLogo';
import backgroundSpreadImg from 'assets/img/backgroundSpreadImg.png';
import { useClerk, useSignIn, useUser } from '@clerk/clerk-react';
import api from '../../../utils/api';

function CleverSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { setActive } = useClerk();
  const { isLoaded, signIn } = useSignIn();
  const { isSignedIn, user } = useUser();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Step 1: Handle Clerk sign-in with token
  const handleSignInWithToken = async () => {
    if (!isLoaded || isSignedIn) return;

    try {
      console.log('Attempting Clerk sign-in with token...');
      const signInAttempt = await signIn.create({
        strategy: 'ticket',
        ticket: token,
      });

      if (signInAttempt.status === 'complete') {
        await setActive({ session: signInAttempt.createdSessionId });
        console.log('Clerk authentication successful');
      }
    } catch (error) {
      console.error('Error authenticating with Clever token:', error);
      setError('Failed to authenticate with Clever. Please try again.');
      setIsProcessing(false);
    }
  };

  // Step 2: Handle backend authentication
  const handleCleverLogin = async () => {
    try {
      const userEmail = user?.primaryEmailAddress?.emailAddress;

      if (!userEmail) {
        throw new Error('No email address found');
      }

      console.log('Calling backend API for user:', userEmail);

      // Call backend API to get auth token and user data
      const response = await api.post('/dashboard/auth/signinWithClerk', {
        email: userEmail,
      });

      if (response?.data?.success || response?.data?.data) {
        const token = response.data.data.token;
        const userData = response.data.data.user;

        // Store authentication data
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem(
          'institution',
          JSON.stringify(userData?.institution[0]),
        );

        toast({
          title: 'Clever Authentication Successful',
          description: 'You have been successfully logged in',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Navigate to main dashboard
        navigate('/admin/peoples', { replace: true });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Backend authentication error:', error);

      let errorMessage =
        'Authentication completed, but there was an issue connecting to our servers.';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);

      toast({
        title: 'Authentication Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Effect 1: Authenticate with Clerk using token
  useEffect(() => {
    if (token) {
      handleSignInWithToken();
    }
  }, [token, isLoaded]);

  // Effect 2: Once Clerk auth is complete, call backend
  useEffect(() => {
    if (isSignedIn && user) {
      handleCleverLogin();
    }
  }, [isSignedIn, user]);

  // Show loading state while processing
  if (isProcessing || !isLoaded) {
    return (
      <Flex
        h="100vh"
        alignItems="center"
        px={{ base: '25px', md: '0px' }}
        flexDirection="column"
        bg={'#FFF8F3'}
        width={'100%'}
      >
        <Flex
          flexDirection={'column'}
          width={'80%'}
          alignItems={'center'}
          backgroundImage={backgroundSpreadImg}
          backgroundRepeat={'no-repeat'}
          backgroundSize={'cover'}
          backgroundPosition={'center'}
          height={'100vh'}
          pt={'68px'}
        >
          <Box mb={'58px'}>
            <TwellaLogoIcon width={'106px'} height={'38px'} />
          </Box>

          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minH="400px"
            gap="4"
          >
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="#EF6506"
              size="xl"
            />
            <Text fontSize="18px" fontWeight="600" color="#222222">
              Completing your login...
            </Text>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  // Show error state if processing failed
  if (error) {
    return (
      <Flex
        h="100vh"
        alignItems="center"
        px={{ base: '25px', md: '0px' }}
        flexDirection="column"
        bg={'#FFF8F3'}
        width={'100%'}
      >
        <Flex
          flexDirection={'column'}
          width={'80%'}
          alignItems={'center'}
          backgroundImage={backgroundSpreadImg}
          backgroundRepeat={'no-repeat'}
          backgroundSize={'cover'}
          backgroundPosition={'center'}
          height={'100vh'}
          pt={'68px'}
        >
          <Box mb={'58px'}>
            <TwellaLogoIcon width={'106px'} height={'38px'} />
          </Box>

          <Flex
            zIndex="2"
            direction="column"
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            bg={'#FFFDFBCC'}
            p={'32px'}
            border={'1px solid #E7E7E7'}
            width={'564px'}
            alignItems="center"
            textAlign="center"
          >
            <Heading
              color={'#E02B00'}
              fontWeight={'700'}
              fontSize="24px"
              mb="16px"
              fontFamily={'DM Sans'}
            >
              Authentication Error
            </Heading>

            <Text fontSize="16px" color="#222222" mb="24px" lineHeight="1.5">
              {error}
            </Text>

            <Flex gap="16px" justifyContent="center">
              <Box
                as="button"
                bg="transparent"
                color="#EF6506"
                px="24px"
                py="12px"
                borderRadius="8px"
                fontSize="14px"
                fontWeight="600"
                border="1px solid #EF6506"
                _hover={{ bg: '#EF650610' }}
                onClick={() => navigate('/')}
              >
                Back to Login
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return null;
}

export default CleverSuccess;
